import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardOrganizer,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useFormattedSalesStatus,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    VerticalEventCard,
    VERTICAL_EVENT_CARD_IMAGE_CONFIG,
} from '@eventbrite/event-renderer';
import React from 'react';
import { DiscoverEventCardProps } from '../../types';

import './DiscoverVerticalEventCard.scss';

export const DiscoverVerticalEventCard: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        moreActions,
        affCode = 'ebdssbdestsearch',
        'data-testid': dataTestId,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: () => props.onClick?.(),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = useFormattedSalesStatus(
        event.eventSalesStatus,
        (event?.repeatingInstanceCount || 0) > 0,
    );

    const shouldShowUrgencySignals = !!urgencySignals && !formattedSalesStatus;

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        eventData: {
            isPromoted: !!event.isPromoted,
            isFree: event.isFree,
            isOnlineEvent: event.isOnlineEvent,
            hasUrgencySignals: shouldShowUrgencySignals,
            id: event.id,
            venue: event.venue,
            tags: event.tags,
        },
    };

    return (
        <section
            className="discover-vertical-event-card"
            data-testid={dataTestId}
        >
            <VerticalEventCard isRaised={isRaised}>
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    {shouldShowUrgencySignals && (
                        <EventCardUrgencySignals
                            className="discover-vertical-event-card__urgency-signals"
                            urgencySignals={urgencySignals}
                        />
                    )}
                    <EventCardLink {...linkProps}>
                        <EventCardImage
                            name={event.name}
                            loading={props.isLazyImage ? 'lazy' : 'eager'}
                            tokens={{
                                ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                                '--image-background-color': event.edgeColor,
                            }}
                            src={event.imageUrl}
                        />
                    </EventCardLink>
                    <EventCardDetails
                        tokens={{
                            '--EventCardDetailsPadding': '16px 8px 12px 12px',
                            '--EventCardDetailsFlexGrow': '2',
                            '--EventCardDetailsPosition': 'relative',
                        }}
                    >
                        <EventCardPromoted
                            isPromoted={event.isPromoted}
                            tokens={{ '--PromotedPaddingBottom': '4px' }}
                        />
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                className="event-card__clamp-line--two eds-text-color--ui-800"
                                title={event.name}
                            />
                        </EventCardLink>
                        <EventCardDate
                            style={{ marginTop: '4px' }}
                            className="eds-text-color--primary-brand"
                            formattedDateString={formattedDateString}
                            variant="body-md-bold"
                        />

                        <EventCardLocation
                            style={{ marginTop: '4px' }}
                            className="event-card__clamp-line--one eds-text-color--ui-600"
                            formattedLocationString={formattedLocationString}
                            variant="body-md"
                        />
                        <EventCardPrice
                            style={{ marginTop: '4px' }}
                            isFree={event.isFree}
                            formattedPriceString={formattedPriceString}
                            className="eds-text-color--ui-600"
                            variant="body-md"
                        />
                        <EventCardOrganizer
                            organizerName={event.organizerName}
                            variantStyle="body-md-bold"
                            className="eds-text-color--ui-800"
                        />

                        <EventCardActions
                            tokens={{
                                '--EventCardActionsPosition': 'absolute',
                                '--EventCardActionsTop': '-22px',
                                '--EventCardActionsRight': '8px',
                            }}
                            actionStyleOverride="outline"
                        >
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />
                            {moreActions}
                        </EventCardActions>
                    </EventCardDetails>
                </EventCardTracking>
            </VerticalEventCard>
        </section>
    );
};
